import React, { useState, useMemo } from "react";
import { api } from "@/lib/api";
import { Input, Select, Btn } from "@/components/UI";
import { Download, Filter } from "lucide-react";
import { openPdfPreview } from "@/lib/pdfPreview";

/**
 * Reusable filter + PDF-report bar for list pages.
 * Props:
 *   reportType: "sales_orders" | "production" | "quality" | "finished_products" | "shipments" | "purchasing"
 *   customers: array of customer docs (optional; if omitted, no customer filter)
 *   statuses: [{value,label}] optional
 *   onFilter: (filteredRows) => void  (client-side filter callback)
 *   rows: source rows
 *   productAccessor: (row) => product name string (used for client-side filter)
 *   customerAccessor: (row) => customer_id
 *   dateAccessor: (row) => iso date string
 *   supplierAccessor?: (row) => supplier string (for purchasing)
 *   statusAccessor?: (row) => status
 */
export function FilterAndReportBar({ reportType, customers, statuses, rows, productAccessor, customerAccessor, dateAccessor, supplierAccessor, statusAccessor, onFiltered, showSupplier = false, onProductQuery, unit = "", showExcel = false }) {
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [custId, setCustId] = useState("");
  const [prodQ, setProdQ] = useState("");
  const [suppQ, setSuppQ] = useState("");
  const [status, setStatus] = useState("");

  // iter100: expose the product-name search string to the parent so it can
  // also filter sibling tabs (e.g. Envanter → Stok Özeti + Malzemeler + Lotlar).
  React.useEffect(() => { onProductQuery?.(prodQ); }, [prodQ]);  // eslint-disable-line

  // Note: accessor props are intentionally OMITTED from deps — they are inline
  // arrow functions that get a new reference on every parent render, which would
  // cause an infinite update loop.  They are functionally stable per page.
  // iter95: Turkish-safe casefold — Standard `.toLowerCase()` breaks "İ→i̇"
  // and "I→i" edge cases so a search for "istanbul" would miss "İSTANBUL".
  // `toLocaleLowerCase("tr")` handles Turkish dotless/dotted i correctly.
  const trLower = (s) => (s || "").toLocaleLowerCase("tr");
  const filtered = useMemo(() => {
    return (rows || []).filter(r => {
      if (dateFrom || dateTo) {
        const d = (dateAccessor?.(r) || "").slice(0, 10);
        if (dateFrom && d < dateFrom) return false;
        if (dateTo && d > dateTo) return false;
      }
      if (custId && customerAccessor && customerAccessor(r) !== custId) return false;
      if (prodQ && productAccessor) {
        const p = trLower(productAccessor(r));
        if (!p.includes(trLower(prodQ))) return false;
      }
      if (suppQ && supplierAccessor) {
        const s = trLower(supplierAccessor(r));
        if (!s.includes(trLower(suppQ))) return false;
      }
      if (status && statusAccessor && statusAccessor(r) !== status) return false;
      return true;
    });
  }, [rows, dateFrom, dateTo, custId, prodQ, suppQ, status]);  // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => { onFiltered?.(filtered); }, [filtered]);  // eslint-disable-line

  const buildParams = () => {
    const params = new URLSearchParams();
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);
    if (custId) params.set("customer_id", custId);
    if (prodQ) params.set("product_name", prodQ);
    if (suppQ) params.set("supplier", suppQ);
    if (status) params.set("status", status);
    if (unit) params.set("unit", unit);
    return params;
  };

  const downloadPdf = async () => {
    try {
      const blob = await api.getBlob(`/reports/${reportType}?${buildParams().toString()}`);
      openPdfPreview(blob, `${reportType}_${dateFrom || "all"}.pdf`);
    } catch (e) { alert(e.response?.data?.detail || "PDF indirilemedi"); }
  };

  const downloadExcel = async () => {
    try {
      // Backend key uses hyphens for multi-word reports (e.g. sales-orders)
      const key = reportType.replace(/_/g, "-");
      const suffix = unit ? `_${unit}` : "";
      await api.download(
        `/reports/${key}/excel?${buildParams().toString()}`,
        `${reportType}${suffix}_${dateFrom || "all"}.xlsx`,
      );
    } catch (e) { alert(e.response?.data?.detail || "Excel indirilemedi"); }
  };

  return (
    <div className="bg-slate-50/60 border border-slate-200 rounded-lg p-3 mb-4 flex flex-wrap items-end gap-2" data-testid={`filter-bar-${reportType}`}>
      <div className="text-slate-600 flex items-center gap-1 text-xs font-semibold uppercase mr-1"><Filter size={12}/> Filtre</div>
      <div className="w-32"><Input label="Başlangıç" type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} data-testid={`flt-from-${reportType}`}/></div>
      <div className="w-32"><Input label="Bitiş" type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} data-testid={`flt-to-${reportType}`}/></div>
      {customers && (
        <div className="w-56"><Select label="Müşteri" value={custId} onChange={e => setCustId(e.target.value)} data-testid={`flt-cust-${reportType}`}>
          <option value="">Tümü</option>{customers.map(c => <option key={c.id} value={c.id}>{c.legal_name}</option>)}
        </Select></div>
      )}
      <div className="w-40"><Input label={showSupplier ? "Malzeme Adı" : "Ürün Adı"} value={prodQ} onChange={e => setProdQ(e.target.value)} placeholder="içerik ara..." data-testid={`flt-prod-${reportType}`}/></div>
      {showSupplier && (
        <div className="w-40"><Input label="Tedarikçi" value={suppQ} onChange={e => setSuppQ(e.target.value)} placeholder="içerik ara..." data-testid={`flt-supp-${reportType}`}/></div>
      )}
      {statuses && statuses.length > 0 && (
        <div className="w-40"><Select label="Durum" value={status} onChange={e => setStatus(e.target.value)} data-testid={`flt-status-${reportType}`}>
          <option value="">Tümü</option>{statuses.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </Select></div>
      )}
      <div className="flex-1"/>
      <Btn onClick={downloadPdf} data-testid={`flt-pdf-${reportType}`} title="Filtrelenmiş Rapor PDF İndir (CustomerERP teması)">
        <Download size={12}/> PDF
      </Btn>
      {showExcel && (
        <Btn variant="secondary" onClick={downloadExcel} data-testid={`flt-excel-${reportType}`} title="Filtrelenmiş Rapor Excel İndir (Envanter şablonu)">
          <Download size={12}/> Excel
        </Btn>
      )}
      <div className="text-xs text-slate-500 self-end pb-1.5">{filtered.length} / {(rows || []).length}</div>
    </div>
  );
}
